import { type SessionSummaryQuery } from "@crowdcoursing/api/graphql";
import {
  Delete as DeleteIcon,
  Email as EmailIcon,
  QrCode as QrCodeIcon,
  Rule as RuleIcon,
  Share as ShareIcon,
  ViewTimeline as ViewTimelineIcon,
} from "@mui/icons-material";
import { Avatar, Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useToast } from "~hooks/useToast";
import { Route } from "~services/router/Route";
import { EntityAvatar } from "~ui/atoms/EntityAvatar/EntityAvatar";
import { AccessibilityChip } from "~ui/molecules/AccessibilityChip/AccessibilityChip";
import { MembershipChip } from "~ui/molecules/MembershipChip/MembershipChip";
import { SearchableChip } from "~ui/molecules/SearchableChip/SearchableChip";
import { BulkEmailModal } from "~ui/organisms/BulkEmail/BulkEmailModal";
import { fullyQualifiedUrl, makeBubbleAvatarUrl } from "~utils/url";

import { EventFormModal } from "../EventFormModal/EventFormModal";

// User-facing strings
const STRINGS = {
  CANCEL_REQUEST: "Cancel Request",
  DELETE_TOOLTIP: "Delete event",
  EDIT_TOOLTIP: "Edit event",
  EMAIL_TOOLTIP: "Send email to event members",
  JOIN_EVENT: "Join",
  LEAVE_EVENT: "Leave",
  LINK_COPIED: "Event link copied to clipboard!",
  MANAGE_RULES_TOOLTIP: "Manage event rules",
  MANAGE_SERIES_TOOLTIP: "Manage event series",
  NOT_SEARCHABLE: "Not Searchable",
  OPEN: "Open",
  PRIVATE: "Private",
  QR_TOOLTIP: "View check-in QR code",
  REQUEST_PENDING: "Request Pending",
  REQUEST_TO_JOIN: "Request to Join",
  SEARCHABLE: "Searchable",
  SHARE_TOOLTIP: "Share event link",
} as const;

type EventFromQuery = NonNullable<SessionSummaryQuery["session"]>;

interface EventHeaderProps {
  event: EventFromQuery;
  members?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
    isFacilitator?: boolean;
    isOwner?: boolean;
    roles: string[];
  }[];
  imageUrl?: string;
  status?: "idle" | "loading" | "error" | "success";
  onImageChange?: (file: File) => void;
  onShareEvent?: () => void;
  onDeleteEvent?: () => void;
  onJoinEvent?: () => Promise<void>;
  onLeaveEvent?: () => Promise<void>;
  onCancelRequest?: () => Promise<void>;
  onUpdateEvent?: (event: {
    bubbleId: string;
    description: string;
    isAutoJoin: boolean;
    isSearchable: boolean;
    name: string;
  }) => void;
  onManageRules?: () => void;
  onManageSeries?: () => void;
  bubbleSelectItems?: { id: string; value: string }[];
  sx?: object;
}

export const EventHeader = ({
  event,
  members = [],
  imageUrl,
  status = "idle",
  onImageChange,
  onShareEvent,
  onDeleteEvent,
  onJoinEvent,
  onLeaveEvent,
  onCancelRequest,
  onUpdateEvent,
  onManageRules,
  onManageSeries,
  bubbleSelectItems = [],
  sx,
}: EventHeaderProps) => {
  const { userIsFacilitator, userIsMember, userIsPending, userIsOwner, isPublic } = event;
  const bubbleAvatarUrl = event.bubbleId ? makeBubbleAvatarUrl(event.bubbleId) : null;
  const toast = useToast();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const handleShareClick = () => {
    const eventUrl = fullyQualifiedUrl(Route.eventView({ eventId: event.id }));
    navigator.clipboard.writeText(eventUrl);
    toast.success(STRINGS.LINK_COPIED);
    onShareEvent?.();
  };

  const handleEmailClick = () => {
    setIsEmailModalOpen(true);
  };

  const handleEmailClose = () => {
    setIsEmailModalOpen(false);
  };

  const handleEmailSend = async () => {
    // The modal's hook will handle the actual sending and toasts
    setIsEmailModalOpen(false);
  };

  // Transform members data for the email modal
  const transformedMembers = members.map((member) => ({
    email: member.email || "",
    id: member.id,
    name: `${member.firstName || ""} ${member.lastName || ""}`.trim(),
    roles: [...member.roles, member.isFacilitator ? "Facilitator" : "Member"],
  }));

  return (
    <Box sx={{ position: "relative" }}>
      {/* Main Event Header */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          height: "100%",
          mb: 3,
          p: 3,
          position: "relative",
          ...sx,
        }}
      >
        {/* Share Button - Upper Right */}
        <Box sx={{ position: "absolute", right: 16, top: 16 }}>
          <Tooltip title={STRINGS.SHARE_TOOLTIP}>
            <IconButton
              onClick={handleShareClick}
              size="small"
              aria-label="Share event link"
              sx={{
                "&:hover": {
                  backgroundColor: "action.selected",
                },
                backgroundColor: "action.hover",
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Stack direction={{ md: "row", xs: "column" }} spacing={3} alignItems={{ md: "flex-start", xs: "center" }}>
          {/* Event Avatar with Organization Badge */}
          <Box
            sx={{
              display: "inline-block",
              mb: { md: 0, xs: 2 },
              position: "relative",
            }}
          >
            <EntityAvatar
              entityName={event.name}
              imageUrl={imageUrl}
              canEdit={userIsFacilitator}
              size="large"
              status={status}
              onImageChange={onImageChange}
            />

            {/* Organization Badge */}
            {bubbleAvatarUrl && (
              <Avatar
                src={bubbleAvatarUrl}
                sx={{
                  border: "3px solid white",
                  bottom: -4,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  height: 32,
                  position: "absolute",
                  right: -4,
                  width: 32,
                  zIndex: 1,
                }}
              />
            )}
          </Box>

          {/* Event Info */}
          <Stack
            direction="column"
            spacing={1}
            sx={{
              alignItems: { md: "flex-start", xs: "center" },
              flex: 1,
              width: { md: "auto", xs: "100%" },
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              data-testid="event-name"
              sx={{
                color: "text.primary",
                lineHeight: 1.2,
                textAlign: { md: "left", xs: "center" },
              }}
            >
              {event.name}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              justifyContent={{ md: "flex-start", xs: "center" }}
              sx={{ width: "100%" }}
            >
              <MembershipChip
                isOwner={userIsOwner}
                isFacilitator={userIsFacilitator}
                isMember={userIsMember}
                isPending={userIsPending}
                isPublic={isPublic}
                onJoin={onJoinEvent}
                onLeave={!userIsOwner ? onLeaveEvent : undefined}
                onCancel={onCancelRequest}
              />
              <AccessibilityChip isPublic={event.isPublic} />
              <SearchableChip isSearchable={event.isSearchable} />
            </Stack>

            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent={{ md: "flex-start", xs: "center" }}
              sx={{ mt: 2, width: "100%" }}
            >
              {userIsFacilitator && (
                <>
                  <Tooltip title={STRINGS.EMAIL_TOOLTIP}>
                    <IconButton
                      onClick={handleEmailClick}
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                        backgroundColor: "action.hover",
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={STRINGS.QR_TOOLTIP}>
                    <IconButton
                      component={Link}
                      to={Route.eventQr({ eventId: event.id })}
                      target="_blank"
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                        backgroundColor: "action.hover",
                      }}
                    >
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {onUpdateEvent && (
                    <EventFormModal
                      mode="edit"
                      onSubmit={onUpdateEvent}
                      bubbleSelectItems={bubbleSelectItems}
                      initialValues={{
                        bubbleId: event.bubbleId,
                        description: event.description,
                        isAutoJoin: event.isPublic,
                        isSearchable: event.isSearchable,
                        name: event.name,
                      }}
                    />
                  )}

                  <Tooltip title={STRINGS.MANAGE_RULES_TOOLTIP}>
                    <IconButton
                      onClick={onManageRules}
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                        backgroundColor: "action.hover",
                      }}
                    >
                      <RuleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={STRINGS.MANAGE_SERIES_TOOLTIP}>
                    <IconButton
                      onClick={() => {
                        console.log("Series icon clicked");
                        onManageSeries?.();
                      }}
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                        backgroundColor: "action.hover",
                      }}
                    >
                      <ViewTimelineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {userIsOwner && (
                    <Tooltip title={STRINGS.DELETE_TOOLTIP}>
                      <IconButton
                        onClick={onDeleteEvent}
                        size="small"
                        sx={{
                          "&:hover": {
                            backgroundColor: "error.light",
                          },
                          backgroundColor: "error.main",
                          color: "white",
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={isEmailModalOpen}
        onClose={handleEmailClose}
        onSend={handleEmailSend}
        contextData={{
          availableRoles: Array.from(new Set([...members.flatMap((member) => member.roles), "Facilitator", "Member"])),
          availableUsers: transformedMembers,
          bubbleId: event.bubbleId || "",
        }}
      />
    </Box>
  );
};
